import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { IParseProduct } from '../models/quotations.models';
import { ProductCodeService } from 'src/app/core/services/product-code.service';

/**
 * Shared client-side export logic for quotation/order products.
 * Used by both the Activity "Products Preview" modal and the Documents tab
 * so the CSV output stays identical across entry points.
 */
@Injectable({ providedIn: 'root' })
export class QuotationExportService {
  constructor(
    private translate: TranslateService,
    private productCode: ProductCodeService
  ) {}

  private getFinalPrice(product: IParseProduct): number {
    return product.final_price
      ? product.final_price
      : product.price_unit * (1 - product.discount / 100);
  }

  private getTotalAmount(product: IParseProduct): number {
    return product.total;
  }

  /** Build a CSV from the given products and trigger a browser download. */
  downloadProductsCsv(products: IParseProduct[], filename = 'quotation_products.csv'): void {
    if (!products || products.length === 0) {
      return;
    }

    // Tenants whose primary identifier is the SKU get a byte-identical file to
    // before WB-7419 — the extra column only appears when it would carry
    // something the SKU column doesn't already show.
    const primaryLabelKey = this.productCode.primaryLabelKey;
    const withPrimaryCode = this.productCode.isCustomPrimary;

    this.translate.get([
      'common.name',
      'inventory.sku',
      primaryLabelKey,
      'quotations_and_orders.quantity',
      'Catalogs.retail_price',
      'quotations_and_orders.discount',
      'Catalogs.final_price',
      'quotations_and_orders.amount',
    ]).pipe(take(1)).subscribe(translations => {
      const headers = [
        translations['common.name'],
        ...(withPrimaryCode ? [translations[primaryLabelKey]] : []),
        translations['inventory.sku'],
        translations['quotations_and_orders.quantity'],
        translations['Catalogs.retail_price'],
        translations['quotations_and_orders.discount'],
        translations['Catalogs.final_price'],
        translations['quotations_and_orders.amount'],
      ];

      const rows = products.map(product => [
        product.name || 'N/A',
        // Primary code first: it's what the user was just reading on screen.
        // The SKU column is kept so nothing is lost from the export.
        ...(withPrimaryCode ? [this.productCode.resolve(product, 'N/A')] : []),
        product.sku || 'N/A',
        product.quantity,
        product.price_unit || product.final_price || 0,
        product.discount || 0,
        this.getFinalPrice(product),
        this.getTotalAmount(product).toFixed(2),
      ]);

      const csvContent = [headers, ...rows]
        .map(row => row.map(item => `"${item ?? ''}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
  }
}
